// Apollo Client cache management utilities

/**
 * Common refetch queries for different entity types
 * Use these in mutations to ensure proper cache invalidation
 */
export const REFETCH_QUERIES = {
  // Style related queries
  STYLES: [
    'GetAllStylesAdmin',
    'GetStyle',
    'GetAllStyles'
  ],
  
  // Prompt related queries
  PROMPTS: [
    'GetAllPromptsAdmin', 
    'GetPrompt',
    'GetAllPrompts'
  ],
  
  // Section related queries
  SECTIONS: [
    'GetAllSectionsAdmin',
    'GetSection', 
    'GetAllSections'
  ],
  
  // Tag related queries
  TAGS: [
    'GetAllTagsAdmin',
    'GetTag',
    'GetAllTags'
  ],
  
  // Category related queries
  CATEGORIES: [
    'GetAllCategoriesAdmin',
    'GetCategory',
    'GetAllCategories'
  ],
  
  // User related queries
  USERS: [
    'GetAllUsers',
    'GetUser'
  ],
  
  // Credit related queries
  CREDITS: [
    'GetAllCredits',
    'GetCredit'
  ],
  
  // Set related queries
  SETS: [
    'GetAllSetsAdmin',
    'GetSet'
  ],
  
  // Model related queries
  MODELS: [
    'GetAllModelsAdmin',
    'GetModel'
  ]
};

/**
 * Helper function to get refetch queries for a specific entity type
 * @param {string} entityType - The entity type (e.g., 'STYLES', 'PROMPTS')
 * @param {string} currentId - Optional current entity ID to include specific query
 * @returns {Array} Array of query names to refetch
 */
export const getRefetchQueries = (entityType, currentId = null) => {
  const queries = REFETCH_QUERIES[entityType] || [];
  
  // Add any additional specific queries if needed
  if (currentId) {
    // The specific queries are already included in the base arrays
    return queries;
  }
  
  return queries;
};

/**
 * Enhanced mutation options with proper cache management
 * @param {string} entityType - The entity type being mutated
 * @param {string} currentId - Optional current entity ID
 * @param {Object} additionalOptions - Additional Apollo mutation options
 * @returns {Object} Mutation options with proper refetch queries
 */
export const getMutationOptions = (entityType, currentId = null, additionalOptions = {}) => {
  return {
    refetchQueries: getRefetchQueries(entityType, currentId),
    awaitRefetchQueries: true,
    errorPolicy: 'all',
    ...additionalOptions
  };
};

/**
 * Cache update helper for optimistic updates
 * @param {Object} client - Apollo Client instance
 * @param {string} queryName - Name of the query to update
 * @param {Object} variables - Query variables
 * @param {Object} newData - New data to write to cache
 */
export const updateQueryCache = (client, queryName, variables, newData) => {
  try {
    client.writeQuery({
      query: queryName,
      variables,
      data: newData
    });
  } catch (error) {
    console.warn(`Failed to update cache for ${queryName}:`, error);
    // Don't throw - cache update failures shouldn't break the app
  }
};

/**
 * Invalidate specific cache entries
 * @param {Object} client - Apollo Client instance
 * @param {Array} queryNames - Array of query names to invalidate
 */
export const invalidateQueries = (client, queryNames) => {
  queryNames.forEach(queryName => {
    try {
      client.cache.evict({ fieldName: queryName });
    } catch (error) {
      console.warn(`Failed to invalidate ${queryName}:`, error);
    }
  });
  
  // Garbage collect to remove orphaned cache entries
  client.cache.gc();
};
